import React, { useEffect, useState } from "react";
import { Scale, Search, CheckCircle2, XCircle, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ADJ_BADGE = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  APPROVED: "bg-green-50 text-green-700 border-green-200",
  REJECTED: "bg-red-50 text-red-600 border-red-200",
};
const AdjBadge = ({ s }) => <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${ADJ_BADGE[s] || ""}`}>{s}</span>;

const QC_OPTS = [
  { v: "passed", l: "QC Passed" }, { v: "waiting_qc", l: "Menunggu QC" },
  { v: "rework", l: "Rework" }, { v: "reject", l: "Reject" },
];

export default function Reconciliation() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ compared: 0, matched: 0, diff: 0 });
  const [q, setQ] = useState("");
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [target, setTarget] = useState(null); // row to adjust

  const load = () => {
    api.get("/reconciliation", { params: { q: q || undefined, only_diff: onlyDiff } })
      .then((r) => { setRows(r.data.rows); setSummary(r.data.summary); });
    api.get("/reconciliation/adjustments").then((r) => setAdjustments(r.data));
  };
  useEffect(() => { api.get("/warehouses").then((r) => setWarehouses(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyDiff]);

  const decide = async (id, action) => {
    try { await api.post(`/reconciliation/adjustments/${id}/${action}`); toast.success(action === "approve" ? "Penyesuaian disetujui & diterapkan" : "Penyesuaian ditolak"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendingCount = adjustments.filter((a) => a.status === "PENDING").length;
  const bulkDecide = async (action) => {
    const msg = action === "approve"
      ? `Setujui & terapkan SEMUA ${pendingCount} pengajuan PENDING sekaligus?`
      : `Tolak SEMUA ${pendingCount} pengajuan PENDING sekaligus?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/reconciliation/adjustments/bulk", { action });
      toast.success(`${r.data.processed} pengajuan ${action === "approve" ? "disetujui & diterapkan" : "ditolak"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const alignAll = async (direction) => {
    const msg = direction === "wms_to_pos"
      ? "Buat pengajuan agar stok WMS mengikuti POS untuk semua varian yang selisih? (butuh approval SPV/Owner)"
      : "Buat pengajuan agar referensi POS mengikuti total WMS untuk semua varian yang selisih? (butuh approval SPV/Owner)";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/reconciliation/align", { direction });
      toast.success(`${r.data.created} pengajuan dibuat, ${r.data.skipped} dilewati (sudah ada pending)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="reconciliation-page">
      <PageHeader title="Rekonsiliasi Stok" subtitle="Bandingkan stok referensi POS vs stok aktual WMS. Penyesuaian butuh persetujuan SPV/Owner." />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Panel className="p-4"><div className="text-xs font-semibold text-wmuted uppercase">Varian Dibandingkan</div><div className="mt-2 text-2xl font-heading font-bold text-cocoa">{summary.compared}</div></Panel>
        <Panel className="p-4"><div className="text-xs font-semibold text-wmuted uppercase">Cocok</div><div className="mt-2 text-2xl font-heading font-bold text-status-passed">{summary.matched}</div></Panel>
        <Panel className="p-4"><div className="text-xs font-semibold text-wmuted uppercase">Selisih</div><div className="mt-2 text-2xl font-heading font-bold text-status-rework">{summary.diff}</div></Panel>
      </div>

      <Tabs defaultValue="compare">
        <TabsList className="bg-white border border-taupe/20">
          <TabsTrigger value="compare" data-testid="tab-compare">Perbandingan</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="tab-adjustments">Pengajuan Penyesuaian</TabsTrigger>
        </TabsList>

        <TabsContent value="compare">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cari produk..." className="pl-9 bg-white" data-testid="recon-search" />
            </div>
            <Button variant="outline" onClick={load} className="border-taupe text-cocoa">Cari</Button>
            <div className="flex items-center gap-2"><Switch checked={onlyDiff} onCheckedChange={setOnlyDiff} data-testid="recon-onlydiff" /><Label>Hanya yang selisih</Label></div>
            <Button variant="outline" onClick={async () => { try { await downloadFile("/reconciliation/export", { q: q || undefined, only_diff: onlyDiff }, "laporan-rekonsiliasi.xlsx"); toast.success("Laporan diunduh"); } catch { toast.error("Gagal mengunduh"); } }} className="border-taupe text-cocoa" data-testid="recon-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
            {can("warehouse_admin") && (
              <>
                <Button onClick={() => alignAll("wms_to_pos")} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="align-wms-btn">Sesuaikan Semua: WMS = POS</Button>
                <Button variant="outline" onClick={() => alignAll("pos_to_wms")} className="border-taupe text-cocoa" data-testid="align-pos-btn">POS = WMS</Button>
              </>
            )}
          </div>

          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead>pos_product_stock_id</TableHead>
                  <TableHead className="text-right">Stok POS (Ref)</TableHead><TableHead className="text-right">Stok Aktual WMS</TableHead>
                  <TableHead className="text-right">Selisih</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.variant_id} data-testid={`recon-row-${r.variant_id}`}>
                    <TableCell className="font-semibold text-cocoa">{r.product_name}</TableCell>
                    <TableCell>{r.color}/{r.size}</TableCell>
                    <TableCell className="font-mono text-xs">{r.pos_product_stock_id || "-"}</TableCell>
                    <TableCell className="text-right text-wmuted">{r.pos_qty}</TableCell>
                    <TableCell className="text-right font-semibold text-cocoa">{r.wms_qty}</TableCell>
                    <TableCell className={`text-right font-bold ${r.diff === 0 ? "text-status-passed" : "text-status-rework"}`}>{r.diff > 0 ? "+" : ""}{r.diff}</TableCell>
                    <TableCell className="text-right">
                      {can("warehouse_admin") && <Button size="sm" variant="outline" onClick={() => setTarget(r)} data-testid={`adjust-${r.variant_id}`} className="border-taupe text-cocoa"><Plus className="w-3.5 h-3.5 mr-1" />Sesuaikan</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10">Belum ada stok WMS untuk dibandingkan. Rekonsiliasi tersedia setelah ada penerimaan/QC.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="adjustments">
          {can("supervisor") && pendingCount > 0 && (
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <div className="text-sm text-amber-800 font-medium">{pendingCount} pengajuan menunggu persetujuan</div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => bulkDecide("approve")} className="bg-status-passed hover:opacity-90 text-white" data-testid="bulk-approve-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Setujui Semua</Button>
                <Button size="sm" variant="outline" onClick={() => bulkDecide("reject")} className="border-status-reject/40 text-status-reject" data-testid="bulk-reject-btn"><XCircle className="w-4 h-4 mr-1.5" /> Tolak Semua</Button>
              </div>
            </div>
          )}
          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nomor</TableHead><TableHead>Produk</TableHead><TableHead>Gudang</TableHead>
                  <TableHead className="text-right">Saat Ini</TableHead><TableHead className="text-right">Target</TableHead>
                  <TableHead>Alasan</TableHead><TableHead>Status</TableHead><TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adjustments.map((a) => (
                  <TableRow key={a.id} data-testid={`adj-row-${a.id}`}>
                    <TableCell className="font-mono text-xs">{a.number}</TableCell>
                    <TableCell className="text-sm">{a.product_name} <span className="text-wmuted">{a.color}/{a.size}</span></TableCell>
                    <TableCell>{a.warehouse_name}</TableCell>
                    <TableCell className="text-right">{a.current_qty}</TableCell>
                    <TableCell className="text-right font-semibold">{a.target_qty}</TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={a.reason}>{a.reason}</TableCell>
                    <TableCell><AdjBadge s={a.status} /></TableCell>
                    <TableCell className="text-xs text-wmuted">{fmtDate(a.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {a.status === "PENDING" && can("supervisor") && (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => decide(a.id, "approve")} data-testid={`approve-adj-${a.id}`}><CheckCircle2 className="w-4 h-4 text-status-passed" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => decide(a.id, "reject")} data-testid={`reject-adj-${a.id}`}><XCircle className="w-4 h-4 text-status-reject" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!adjustments.length && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-10">Belum ada pengajuan penyesuaian.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
        </TabsContent>
      </Tabs>

      {target && <AdjustDialog row={target} warehouses={warehouses} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />}
    </div>
  );
}

function AdjustDialog({ row, warehouses, onClose, onDone }) {
  const [form, setForm] = useState({ warehouse_id: "", qc_status: "passed", target_qty: row.wms_qty, reason: "" });
  const submit = async () => {
    if (!form.warehouse_id) return toast.error("Pilih gudang");
    if (!form.reason.trim()) return toast.error("Alasan wajib diisi");
    try {
      await api.post("/reconciliation/adjustments", { variant_id: row.variant_id, warehouse_id: form.warehouse_id, qc_status: form.qc_status, target_qty: parseInt(form.target_qty) || 0, reason: form.reason });
      toast.success("Pengajuan penyesuaian dibuat (menunggu approval SPV/Owner)"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Ajukan Penyesuaian Stok</DialogTitle></DialogHeader>
        <div className="text-sm mb-2 text-cocoa">{row.product_name} · {row.color}/{row.size}</div>
        <div className="text-xs text-wmuted mb-3">Stok POS (referensi): {row.pos_qty} · Stok WMS saat ini: {row.wms_qty}</div>
        <div className="space-y-3">
          <div><Label>Gudang *</Label>
            <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
              <SelectTrigger data-testid="adj-warehouse"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Bucket Status QC</Label>
            <Select value={form.qc_status} onValueChange={(v) => setForm({ ...form, qc_status: v })}>
              <SelectTrigger data-testid="adj-qcstatus"><SelectValue /></SelectTrigger>
              <SelectContent>{QC_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Target Qty (di gudang & bucket terpilih) *</Label><Input type="number" value={form.target_qty} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} data-testid="adj-target" /></div>
          <div><Label>Alasan *</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Contoh: hasil stock opname fisik" data-testid="adj-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} data-testid="submit-adjustment-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Ajukan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
